<script setup lang="ts">
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import { useAffiliationUpload } from "~/modules/relation/affiliation/composables/useAffiliationUpload";
import affiliationService from "~/modules/relation/affiliation/services/affiliation.service";
import type { CreateRelationByUploadingIdCardPayload } from "~/modules/relation/models/CreateRelationByUploadingIdCardPayload.interface";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";

const props = defineProps<
  GenericParameters & {
    fetchAffiliationChildrenResponse?: UseFetchResponse<RelationEntity>;
  }
>();

const {
  isLoading: isLoadingUploadIdCard,
  error: errorUploadIdCard,
  showUploadIdCardModal,
  canCreateAffiliationByUploadingIdCard,
  uploadIdCard,
} = useAffiliationUpload({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});

const showCreateOrganizationSideCanvas = ref(false);

async function createAffiliationByUploadingIdCard(
  $event: CreateRelationByUploadingIdCardPayload,
) {
  await uploadIdCard($event);
  if (!errorUploadIdCard.value) {
    props.fetchAffiliationChildrenResponse?.search();
  }
}

/**
 * Factory function that creates a delete function for a specific organization affiliation.
 *
 * This is a higher-order function that:
 * 1. Takes an organizationCode as parameter
 * 2. Returns an async function that performs the deletion for that specific organization
 * 3. The returned function captures the organizationCode in its closure
 *
 * @param targetOrganizationCode - The code of the organization affiliation to delete
 * @returns A parameterless async function that deletes the specified affiliation
 *
 * @example
 * // Create a specific delete function for organization 123
 * const deleteOrg123 = getDeleteFn(123);
 * // Later, execute the deletion
 * await deleteOrg123();
 */
function getDeleteFn(targetOrganizationCode: number) {
  return async () => {
    if (props.organizationCode !== undefined) {
      await affiliationService.deleteOrganizationAffiliation({
        organizationCode: props.organizationCode,
        targetOrganizationCode,
      });
    } else if (props.businessUnitCode !== undefined) {
      await affiliationService.deleteBusinessUnitAffiliation({
        businessUnitCode: props.businessUnitCode,
        targetOrganizationCode,
      });
    }
  };
}
</script>

<template>
  <div class="w-full flex flex-col gap-4">
    <relation-add-bar
      v-model:show-upload-id-card-modal="showUploadIdCardModal"
      context="affiliation"
      :is-loading="isLoadingUploadIdCard"
      :disabled="!canCreateAffiliationByUploadingIdCard()"
      @upload-id-card="createAffiliationByUploadingIdCard"
      @create-organization="showCreateOrganizationSideCanvas = true"
    />

    <relation-entity-list
      from-to="to"
      :pagination="fetchAffiliationChildrenResponse?.results?.value?.pagination"
      :relations="fetchAffiliationChildrenResponse?.results?.value?.results"
      :is-loading="fetchAffiliationChildrenResponse?.isLoading.value"
      :error="fetchAffiliationChildrenResponse?.error.value"
      :delete-fn="getDeleteFn"
      @change-page-and-page-size="fetchAffiliationChildrenResponse?.fetchData"
      @delete-success="fetchAffiliationChildrenResponse?.fetchData"
    />
  </div>

  <organization-create
    v-if="businessUnitCode"
    v-model:show="showCreateOrganizationSideCanvas"
    :business-unit-code="businessUnitCode"
    create-affiliation
    :target-organization-code="organizationCode"
    @create:success="fetchAffiliationChildrenResponse?.fetchData"
  />
</template>
